import { definePreset } from '@primevue/themes';
import { BasePrimeVuePreset } from '@peanuds/app-shell/plugins/prime-vue';

const FORM_FIELD_PADDING = {
  paddingX: 'var(--customer-form-field-padding-x)',
  paddingY: 'var(--customer-form-field-padding-y)',
};

const SMALL_PADDING = {
  paddingX: 'var(--customer-small-padding-x)',
  paddingY: 'var(--customer-small-padding-y)',
};

const FORM_FIELD_SHAPE = {
  borderRadius: 'var(--customer-form-field-border-radius)',
  ...FORM_FIELD_PADDING,
  sm: SMALL_PADDING,
  lg: FORM_FIELD_PADDING,
};

// Figma draws the label black on the mint / coral idle fill and white once the
// fill darkens on hover and press.
const filledButton = (severity: 'primary' | 'danger') => ({
  background: `var(--color-button-${severity}-idle)`,
  hoverBackground: `var(--color-button-${severity}-hover)`,
  activeBackground: `var(--color-button-${severity}-active)`,
  borderColor: `var(--color-button-${severity}-idle)`,
  hoverBorderColor: `var(--color-button-${severity}-hover)`,
  activeBorderColor: `var(--color-button-${severity}-active)`,
  color: 'var(--customer-on-primary)',
  hoverColor: 'var(--color-base-white)',
  activeColor: 'var(--color-base-white)',
});

export const clearBankDemoPrimeVuePreset = definePreset(BasePrimeVuePreset, {
  semantic: {
    colorScheme: {
      light: {
        highlight: {
          background: 'var(--customer-highlight-bg)',
          focusBackground: 'var(--customer-highlight-bg)',
          color: 'var(--color-border-active)',
          focusColor: 'var(--color-border-active)',
        },
      },
    },
    formField: {
      paddingX: 'var(--customer-form-field-padding-x, 0.75rem)',
      paddingY: 'var(--customer-form-field-padding-y, 0.5rem)',
      borderRadius: 'var(--customer-form-field-border-radius, var(--p-border-radius-md))',
      transitionDuration: 'var(--customer-form-field-transition-duration, 0.2s)',
    },
    overlay: {
      select: {
        borderRadius:
          'var(--customer-dropdown-overlay-border-radius, var(--customer-form-field-border-radius, var(--p-border-radius-md)))',
        shadow:
          'var(--customer-dropdown-overlay-shadow, 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1))',
      },
    },
    list: {
      padding: 'var(--customer-dropdown-list-padding, 0.25rem 0.25rem)',
      gap: 'var(--customer-dropdown-list-gap, 2px)',
      option: {
        padding: 'var(--customer-dropdown-option-padding, 0.5rem 0.75rem)',
        borderRadius: 'var(--customer-dropdown-option-border-radius, var(--p-border-radius-sm))',
      },
    },
  },
  components: {
    inputtext: { root: FORM_FIELD_SHAPE },
    select: { root: FORM_FIELD_SHAPE },
    multiselect: { root: FORM_FIELD_SHAPE },
    textarea: { root: FORM_FIELD_SHAPE },
    button: {
      root: {
        borderRadius: 'var(--customer-button-border-radius, var(--p-border-radius-absolut))',
        roundedBorderRadius:
          'var(--customer-button-rounded-border-radius, var(--customer-button-border-radius, var(--p-border-radius-absolut)))',
        gap: 'var(--customer-button-gap, var(--spacing-s))',
        paddingX: 'var(--customer-button-padding-x, var(--spacing-sm))',
        paddingY: 'var(--customer-button-padding-y, 0.625rem)',
        iconOnlyWidth: '3rem',
        sm: {
          ...SMALL_PADDING,
          iconOnlyWidth: '2.25rem',
        },
        label: {
          fontWeight: 'var(--customer-button-label-font-weight, 500)',
        },
      },
      colorScheme: {
        light: {
          root: {
            primary: filledButton('primary'),
            danger: filledButton('danger'),
          },
          outlined: {
            primary: {
              borderColor: 'var(--color-button-outlined-border)',
              color: 'var(--color-text-primary)',
              hoverBackground: 'var(--color-button-outlined-hover)',
              activeBackground: 'var(--color-button-outlined-active)',
            },
          },
        },
      },
    },
    menubar: {
      colorScheme: {
        root: {
          background: 'var(--color-bg-sidebar)',
          borderColor: 'transparent',
          borderRadius: 'var(--radius-999)',
        },
      },
    },
  },
});

export default clearBankDemoPrimeVuePreset;
